import { useState, useRef } from "react";
import { DashboardLayout } from "@/pages/Dashboard";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Image, Trash2, CheckCircle } from "lucide-react";

const Settings = () => {
  const { user, checkAuth } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
      toast.error("Please upload a PNG or JPG image");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      await axios.post(`${API}/user/upload-logo`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Logo uploaded successfully!");
      // Refresh user data
      await checkAuth();
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getFullLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;
    if (logoUrl.startsWith("http")) return logoUrl;
    // Use window.location.origin for deployment compatibility across environments
    return `${window.location.origin}${logoUrl}`;
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Settings
          </h1>
          <p className="text-muted-foreground">Manage your account and branding settings</p>
        </div>

        {/* Profile Section */}
        <Card className="bg-white mb-6">
          <CardHeader>
            <CardTitle className="text-base">Account Information</CardTitle>
            <CardDescription>Your profile information from Google</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-16 h-16 rounded-full"
                />
              )}
              <div>
                <p className="font-semibold text-foreground">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Business Logo Section */}
        <Card className="bg-white mb-6">
          <CardHeader>
            <CardTitle className="text-base">Business Logo</CardTitle>
            <CardDescription>Upload your business logo to appear on invoices</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Current Logo Preview */}
              {user?.business_logo_url ? (
                <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                  <img 
                    src={getFullLogoUrl(user.business_logo_url)} 
                    alt="Business Logo" 
                    className="w-24 h-24 object-contain bg-white rounded border"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-foreground flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Logo uploaded
                    </p>
                    <p className="text-sm text-muted-foreground">
                      This logo will appear on your invoice PDFs
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                  <div className="w-24 h-24 bg-white rounded border flex items-center justify-center">
                    <Image className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-muted-foreground">No logo uploaded</p>
                    <p className="text-sm text-muted-foreground">
                      Upload a logo to brand your invoices
                    </p>
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/png,image/jpeg,image/jpg"
                  className="hidden"
                  data-testid="logo-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  variant={user?.business_logo_url ? "outline" : "default"}
                  data-testid="upload-logo-btn"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {user?.business_logo_url ? "Change Logo" : "Upload Logo"}
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Accepted formats: PNG, JPG. Max size: 5MB
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Settings Section */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-base">Invoice Settings</CardTitle>
            <CardDescription>Your invoice numbering sequence</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Invoice Number Format</p>
                  <p className="text-sm text-muted-foreground">INV-0001, INV-0002, etc.</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Current sequence</p>
                  <p className="font-mono font-semibold text-primary">
                    INV-{String(user?.invoice_sequence_counter || 0).padStart(4, '0')}
                  </p>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Invoice numbers are automatically generated when you create new invoices
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
